import * as React from "react";
import { Check } from "lucide-react";

import type { AuthoringFailedEdit, InstructionsRunReport } from "../documentAuthoring.types";
import { plural } from "./helpers";

interface Props {
  report: InstructionsRunReport;
  aiAuthor: string;
}

export const FailedEditRow: React.FC<{ edit: AuthoringFailedEdit }> = ({ edit }) => (
  <li className="space-y-1 text-xs">
    <p className="break-words">
      <del className="text-danger">{edit.target_text}</del>
      {edit.new_text && edit.new_text !== edit.target_text && (
        <>
          {" "}
          <ins className="text-success no-underline">{edit.new_text}</ins>
        </>
      )}
    </p>
    {edit.comment && <p className="text-gray-600">{edit.comment}</p>}
    <details>
      <summary className="cursor-pointer text-gray-500">Why it failed</summary>
      <pre className="mt-1 whitespace-pre-wrap rounded bg-ivory-mist-200 p-2 text-xxs text-gray-700">{edit.error}</pre>
    </details>
  </li>
);

/** What the last instructions run did (mirrors the PoC's report panel). */
export const RunReport: React.FC<Props> = ({ report, aiAuthor }) => (
  <section aria-label="Last review" className="space-y-3 px-4 py-3">
    <h2 className="text-xxs font-semibold uppercase tracking-wide text-gray-500">Last review</h2>
    {report.summary && <p className="text-sm text-ui-paragraph-text">{report.summary}</p>}

    {report.applied.length > 0 ? (
      <>
        <p className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
          <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
          {plural(report.applied.length, "edit added", "edits added")} as tracked changes by {report.author}
        </p>
        <ol className="space-y-2">
          {report.applied.map((edit, index) => (
            <li key={index} className="rounded-md border border-desert-sand-300 bg-white px-3 py-2 text-xs">
              {edit.location && (
                <p className="text-xxs font-semibold uppercase tracking-wide text-gray-500">
                  {edit.location.split(" > ").pop()}
                </p>
              )}
              <p className="break-words text-ui-paragraph-text">{edit.comment || edit.new_text}</p>
            </li>
          ))}
        </ol>
      </>
    ) : (
      <p className="text-xs text-gray-500">No edits were added.</p>
    )}

    {report.failed.length > 0 && (
      <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
        <p className="text-xs font-semibold text-amber-900">
          {plural(report.failed.length, "edit couldn't", "edits couldn't")} be placed
        </p>
        <p className="text-xs text-amber-800">
          {aiAuthor} couldn't find the exact text for these. Apply them by hand if you want them.
        </p>
        <ul className="space-y-2">
          {report.failed.map((edit, index) => (
            <FailedEditRow key={index} edit={edit} />
          ))}
        </ul>
      </div>
    )}
  </section>
);
